import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, ChevronLeft, ChevronRight, BellRing } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["ACTIVE", "EXPIRING_SOON", "EXPIRED", "PENDING", "SUSPENDED", "INACTIVE", "REVOKED", "BELUM_DITUNTUT"];
const PAGE_SIZE = 20;

export default function Ahli() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  const canManage = ["super_admin", "secretariat", "president"].includes(user?.role);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = async (pageToLoad) => {
    setLoading(true);
    try {
      const res = await api.get("/members", { params: { search: search || undefined, status: status !== "all" ? status : undefined, page: pageToLoad, limit: PAGE_SIZE } });
      setItems(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(page); }, [status, page]);

  const runSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(1);
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await api.post("/members/send-expiry-reminders");
      toast.success(`${res.data.sent} e-mel peringatan dihantar drpd ${res.data.total_candidates} ahli akan/telah tamat tempoh.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingReminders(false);
    }
  };

  return (
    <div data-testid="admin-ahli-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="font-display text-2xl font-bold text-piks-black">Ahli</h1>
        {canManage && (
          <Button size="sm" variant="outline" onClick={sendReminders} disabled={sendingReminders} data-testid="ahli-send-reminders-btn">
            <BellRing className="w-3.5 h-3.5 mr-1.5" /> {sendingReminders ? "Menghantar..." : "Hantar Peringatan Sekarang"}
          </Button>
        )}
      </div>
      <p className="text-sm text-piks-muted mb-6">{total} ahli berdaftar.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={runSearch} className="flex gap-2 flex-1 max-w-sm">
          <Input data-testid="ahli-search-input" placeholder="Cari nama, No. Ahli, No. KP, syarikat" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-white" />
        </form>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); setSearchParams(v === "all" ? {} : { status: v }); }}>
          <SelectTrigger data-testid="ahli-status-filter" className="w-48 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-piks-muted text-sm">Memuatkan...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-piks-muted bg-white rounded-xl border border-piks-border" data-testid="ahli-empty-state">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" /> Tiada ahli dijumpai.
        </div>
      ) : (
        <>
          <div className="hidden sm:block bg-white rounded-xl border border-piks-border overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-piks-border text-left text-xs text-piks-muted uppercase tracking-wide">
                  <th className="p-4">No. Ahli</th><th className="p-4">Nama</th><th className="p-4">Syarikat</th><th className="p-4">Status</th><th className="p-4">Sah Sehingga</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="border-b border-piks-border last:border-0 hover:bg-piks-ivory/50" data-testid={`ahli-row-${m.id}`}>
                    <td className="p-4 font-mono text-piks-black">{m.member_number}</td>
                    <td className="p-4 font-medium text-piks-black">{m.full_name || "-"}</td>
                    <td className="p-4 text-piks-muted">{m.company_name || "-"}</td>
                    <td className="p-4"><StatusBadge status={m.effective_status || m.status} /></td>
                    <td className="p-4 text-piks-muted">{m.valid_until ? new Date(m.valid_until).toLocaleDateString("ms-MY") : "-"}</td>
                    <td className="p-4"><Link to={`/admin/ahli/${m.id}`} className="text-piks-black font-semibold hover:text-piks-red" data-testid={`ahli-view-${m.id}`}>Lihat</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {items.map((m) => (
              <Link to={`/admin/ahli/${m.id}`} key={m.id} className="block p-4 rounded-xl bg-white border border-piks-border" data-testid={`ahli-card-${m.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-piks-black">{m.full_name || "-"}</p>
                    <p className="text-xs font-mono text-piks-muted">{m.member_number}</p>
                  </div>
                  <StatusBadge status={m.effective_status || m.status} />
                </div>
                <p className="text-xs text-piks-muted">{m.company_name || "-"}</p>
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mt-5" data-testid="ahli-pagination">
            <p className="text-xs text-piks-muted" data-testid="ahli-pagination-info">
              Halaman {page} drpd {totalPages} ({total} ahli)
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="ahli-prev-page-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Sebelum
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="ahli-next-page-btn">
                Seterusnya <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
